"use client";

import { tagBackgroundClass } from "./tag-colors";
import React, { useState, useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import type { CalendarEvent } from "./calendar";
import UserSvg from "@/assets/userSvg.svg";
import { CalendarEventActions } from "./calendar-event-actions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, CalendarDays, Clock, MapPin, X } from "lucide-react";

interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  dateRange: { start: Date; end: Date };
  onViewEvent?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  refetch?: () => void;
  access?: string;
  hideEventActions?: boolean;
}

// Map color to static Tailwind classes
const getColorClasses = (color?: string) => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-200 border-purple-400 text-purple-900 hover:bg-purple-250",
    blue: "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250",
    green: "bg-green-200 border-green-400 text-green-900 hover:bg-green-250",
    red: "bg-red-200 border-red-400 text-red-900 hover:bg-red-250",
    yellow: "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-250",
    orange: "bg-orange-200 border-orange-400 text-orange-900 hover:bg-orange-250",
    teal: "bg-teal-200 border-teal-400 text-teal-900 hover:bg-teal-250",
    pink: "bg-pink-200 border-pink-400 text-pink-900 hover:bg-pink-250",
    indigo: "bg-indigo-200 border-indigo-400 text-indigo-900 hover:bg-indigo-250",
    gray: "bg-gray-200 border-gray-400 text-gray-900 hover:bg-gray-250",
  };
  return colorMap[color || ""] || "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250";
};

// Map color to drawer card classes
const getDrawerCardColorClasses = (color?: string) => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-100/70 border-purple-200 text-purple-950 hover:bg-purple-100",
    blue: "bg-blue-100/70 border-blue-200 text-blue-950 hover:bg-blue-100",
    green: "bg-emerald-100/70 border-emerald-200 text-emerald-950 hover:bg-emerald-100",
    red: "bg-rose-100/70 border-rose-200 text-rose-950 hover:bg-rose-100",
    yellow: "bg-amber-100/70 border-amber-200 text-amber-950 hover:bg-amber-100",
    orange: "bg-orange-100/70 border-orange-200 text-orange-950 hover:bg-orange-100",
    teal: "bg-teal-100/70 border-teal-200 text-teal-950 hover:bg-teal-100",
    pink: "bg-pink-100/70 border-pink-200 text-pink-950 hover:bg-pink-100",
    indigo: "bg-indigo-100/70 border-indigo-200 text-indigo-950 hover:bg-indigo-100",
    gray: "bg-gray-100/70 border-gray-200 text-gray-900 hover:bg-gray-150",
  };
  return colorMap[color || ""] || "bg-blue-100/70 border-blue-200 text-blue-950 hover:bg-blue-100";
};

// Maximum slots shown per day cell before grouping into "+ N more"
const MAX_VISIBLE_EVENTS = 3;

export function MonthView({
  currentDate,
  events,
  dateRange,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  onDateClick,
  refetch,
  access,
  hideEventActions,
}: MonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);
  const [sheetSearch, setSheetSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "available" | "booked">("all");

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Pre-index events by day string "yyyy-MM-dd" for O(1) instant lookup
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const startVal = event.start;
      let dateKey = "";
      if (typeof startVal === "string") {
        dateKey = startVal.substring(0, 10);
      } else if (startVal instanceof Date && !isNaN(startVal.getTime())) {
        const y = startVal.getFullYear();
        const m = String(startVal.getMonth() + 1).padStart(2, "0");
        const d = String(startVal.getDate()).padStart(2, "0");
        dateKey = `${y}-${m}-${d}`;
      }
      if (dateKey) {
        let list = map.get(dateKey);
        if (!list) {
          list = [];
          map.set(dateKey, list);
        }
        list.push(event);
      }
    }
    return map;
  }, [events]);

  // Derived key for the currently selected day to dynamically stay in sync with events
  const selectedDayKey = useMemo(() => {
    if (!selectedDayDate) return "";
    const y = selectedDayDate.getFullYear();
    const m = String(selectedDayDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDayDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedDayDate]);

  // Dynamic active day events that always update when events change (e.g. on save/remove)
  const activeDayEvents = useMemo(() => {
    if (!selectedDayKey) return [];
    return eventsByDay.get(selectedDayKey) || [];
  }, [selectedDayKey, eventsByDay]);

  // Filtered events inside the Day Slots Sheet
  const filteredSheetEvents = useMemo(() => {
    if (!selectedDayDate) return [];
    return activeDayEvents.filter((ev) => {
      const matchesSearch =
        !sheetSearch ||
        ev.title?.toLowerCase().includes(sheetSearch.toLowerCase()) ||
        ev.description?.toLowerCase().includes(sheetSearch.toLowerCase()) ||
        ev.user?.name?.toLowerCase().includes(sheetSearch.toLowerCase());

      if (!matchesSearch) return false;

      const isAvailable = ev.tags?.some((t) => {
        const lbl = t.label?.toLowerCase();
        return lbl === "available" || (t.color === "green" && lbl !== "removed");
      });
      if (statusFilter === "available") return isAvailable;
      if (statusFilter === "booked") return !isAvailable;
      return true;
    });
  }, [selectedDayDate, activeDayEvents, sheetSearch, statusFilter]);

  const sheetAvailableCount = useMemo(() => {
    return activeDayEvents.filter((ev) =>
      ev.tags?.some((t) => {
        const lbl = t.label?.toLowerCase();
        return lbl === "available" || (t.color === "green" && lbl !== "removed");
      })
    ).length;
  }, [activeDayEvents]);

  const sheetBookedCount = useMemo(() => {
    return activeDayEvents.length - sheetAvailableCount;
  }, [activeDayEvents, sheetAvailableCount]);

  // Create calendar rows
  const rows = [];
  let days = [];
  let day = startDate;

  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      const currentDay = day;
      const y = currentDay.getFullYear();
      const m = String(currentDay.getMonth() + 1).padStart(2, "0");
      const d = String(currentDay.getDate()).padStart(2, "0");
      const dayKey = `${y}-${m}-${d}`;

      const dayEvents = eventsByDay.get(dayKey) || [];
      const isCurrentMonth = isSameMonth(day, monthStart);
      const isPast = currentDay < startOfDay(new Date());
      const isToday = isSameDay(currentDay, new Date());
      const isClickable = onDateClick && (!isPast || isToday);

      const hasOverflow = dayEvents.length > MAX_VISIBLE_EVENTS;
      const visibleEvents = hasOverflow
        ? dayEvents.slice(0, MAX_VISIBLE_EVENTS - 1)
        : dayEvents;
      const remainingCount = dayEvents.length - visibleEvents.length;

      days.push(
        <div
          key={day.toString()}
          className={cn(
            "border-r border-b border-gray-200 min-h-[90px] sm:min-h-[120px] p-1 sm:p-1.5 group flex flex-col justify-between",
            !isCurrentMonth && "bg-gray-50/40 text-gray-400",
            isPast && !isToday && "bg-gray-100/40 grayscale-[0.4]",
            i === 3 && "bg-green-50/20",
            isClickable ? "cursor-pointer" : "cursor-default"
          )}
          onClick={(e) => {
            if (
              isClickable &&
              e.target instanceof Element &&
              !e.target.closest("[data-calendar-event]") &&
              !e.target.closest("[data-more-button]")
            ) {
              onDateClick(new Date(currentDay));
            }
          }}
        >
          <div className="flex flex-col h-full">
            {/* Header: Date Number */}
            <div className="flex items-center justify-between mb-1">
              <div
                className={cn(
                  "text-[11px] sm:text-xs w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full transition-colors font-medium shrink-0",
                  isToday
                    ? "bg-primary text-white font-bold"
                    : !isCurrentMonth
                    ? "text-gray-400"
                    : "text-gray-700",
                  onDateClick && "group-hover:bg-primary/10 group-hover:text-primary"
                )}
              >
                {format(day, "d")}
                {day.getDate() === 1 && (
                  <span className="hidden sm:inline text-[10px] text-gray-500 ml-1">
                    {format(day, "MMM")}
                  </span>
                )}
              </div>

              {dayEvents.length > 0 && (
                <button
                  type="button"
                  data-more-button="true"
                  className="text-[9px] sm:text-[10px] text-gray-500 hover:text-primary font-medium transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDayDate(new Date(currentDay));
                    setSheetSearch("");
                    setStatusFilter("all");
                  }}
                >
                  {dayEvents.length} {dayEvents.length === 1 ? "slot" : "slots"}
                </button>
              )}
            </div>

            {/* Event Pills List (Limited to prevent DOM explosion) */}
            <div className="flex-1 space-y-1 overflow-hidden">
              {visibleEvents.map((event) => (
                <div
                  key={event.id}
                  data-calendar-event="true"
                  className={cn(
                    "text-xs p-1 rounded border shadow-2xs transition-colors",
                    getColorClasses(event.color),
                    onViewEvent && "cursor-pointer"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewEvent?.(event);
                  }}
                >
                  <div className="flex items-start gap-1">
                    {event.user?.avatar && (
                      <Avatar className="h-3.5 w-3.5 hidden sm:flex shrink-0">
                        <AvatarImage
                          src={`${import.meta.env.VITE_API_URL}/uploads/${event.user.avatar}`}
                          alt={event.user.name || "User"}
                        />
                        <AvatarFallback>
                          <img src={UserSvg} alt="User" className="h-3.5 w-3.5 rounded-full" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className="font-medium flex-1 min-w-0 cursor-pointer break-words leading-tight text-[9px] sm:text-[11px] line-clamp-1"
                      title={event.title}
                    >
                      {event.title}
                    </div>
                    {!hideEventActions && (
                      <div
                        className="hidden sm:block shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CalendarEventActions
                          event={event}
                          onEdit={onEditEvent}
                          onView={onViewEvent}
                          onDelete={onDeleteEvent}
                          refetch={refetch}
                          access={access}
                          size="sm"
                          variant="dropdown"
                        />
                      </div>
                    )}
                  </div>

                  {event.tags && (
                    <div className="hidden sm:flex flex-wrap gap-1 mt-0.5">
                      {event.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className={cn(
                            "inline-flex items-center justify-center whitespace-nowrap px-1.5 py-0.5 rounded text-white text-[9px] font-medium leading-none",
                            tagBackgroundClass(tag.color)
                          )}
                        >
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* "+ N more slots" trigger button */}
              {hasOverflow && (
                <button
                  type="button"
                  data-more-button="true"
                  className="w-full py-0.5 sm:py-1 px-1.5 rounded bg-blue-50/90 hover:bg-blue-100 active:bg-blue-200 text-blue-700 font-semibold text-[9px] sm:text-[11px] text-center border border-blue-200 transition-colors flex items-center justify-center gap-1 shadow-2xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDayDate(new Date(currentDay));
                    setSheetSearch("");
                    setStatusFilter("all");
                  }}
                >
                  <span>+{remainingCount} more slots</span>
                </button>
              )}
            </div>
          </div>
        </div>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <div key={day.toString()} className="grid grid-cols-7">
        {days}
      </div>
    );
    days = [];
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="sticky top-0 z-10 grid grid-cols-7 border-b bg-white shrink-0">
        {daysOfWeek.map((dName, index) => (
          <div
            key={dName}
            className={cn(
              "py-1.5 sm:py-2 text-center text-[10px] sm:text-[13px] border-r truncate px-0.5 font-semibold text-gray-700",
              index === 3 && "bg-green-50/30"
            )}
          >
            <span className="sm:hidden">{dName.substring(0, 3)}</span>
            <span className="hidden sm:inline">{dName}</span>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">{rows}</div>

      {/* ── Day Slots Drawer for viewing all slots without lagging ── */}
      <Sheet
        open={Boolean(selectedDayDate)}
        onOpenChange={(open) => !open && setSelectedDayDate(null)}
      >
        <SheetContent
          fixCloseButton
          reverseCloseButton
          className="w-full sm:max-w-md md:max-w-lg p-0 flex flex-col h-full bg-slate-50"
        >
          {/* Header */}
          <SheetHeader className="p-4 border-b bg-white shrink-0 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <CalendarDays className="size-4 text-primary" />
                  {selectedDayDate &&
                    format(selectedDayDate, "EEEE, MMMM d, yyyy")}
                </SheetTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Total {activeDayEvents.length} scheduled slots on this day
                </p>
              </div>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search slot, facility, coach, or member..."
                value={sheetSearch}
                onChange={(e) => setSheetSearch(e.target.value)}
                className="pl-8 h-8 text-xs bg-slate-50"
              />
              {sheetSearch && (
                <button
                  type="button"
                  onClick={() => setSheetSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>

            {/* Quick Status Filters */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={statusFilter === "all" ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs px-2.5"
                onClick={() => setStatusFilter("all")}
              >
                All ({activeDayEvents.length})
              </Button>
              <Button
                type="button"
                variant={statusFilter === "available" ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-7 text-xs px-2.5",
                  statusFilter === "available"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                )}
                onClick={() => setStatusFilter("available")}
              >
                Available ({sheetAvailableCount})
              </Button>
              <Button
                type="button"
                variant={statusFilter === "booked" ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-7 text-xs px-2.5",
                  statusFilter === "booked"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "text-red-700 border-red-200 hover:bg-red-50"
                )}
                onClick={() => setStatusFilter("booked")}
              >
                Booked / Reserved ({sheetBookedCount})
              </Button>
            </div>
          </SheetHeader>

          {/* Scrollable Slots List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {filteredSheetEvents.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground">
                No slots match the search or filter criteria.
              </div>
            ) : (
              filteredSheetEvents.map((event) => (
                <div
                  key={event.id}
                  className={cn(
                    "p-3 rounded-xl border shadow-xs hover:shadow-md transition-all cursor-pointer",
                    getDrawerCardColorClasses(event.color)
                  )}
                  onClick={() => onViewEvent?.(event)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Top Row: Time range + Slot Badges */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-800 shrink-0 mr-1">
                          <Clock className="size-3.5 text-gray-500 shrink-0" />
                          <span>
                            {event.start instanceof Date
                              ? format(event.start, "HH:mm")
                              : String(event.start).slice(11, 16)}{" "}
                            –{" "}
                            {event.end instanceof Date
                              ? format(event.end, "HH:mm")
                              : String(event.end).slice(11, 16)}
                          </span>
                        </div>

                        {event.tags && event.tags.length > 0 && (
                          <div className="flex flex-wrap items-center gap-0.5">
                            {event.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className={cn(
                                  "inline-flex items-center justify-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-tight shadow-2xs leading-normal",
                                  tag.color === "green"
                                    ? "bg-emerald-500 text-white"
                                    : tag.color === "red"
                                    ? "bg-rose-500 text-white"
                                    : tag.color === "orange"
                                    ? "bg-amber-500 text-white"
                                    : "bg-gray-500/80 text-white"
                                )}
                              >
                                {tag.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Title: Membership & Facility name */}
                      <p className="font-semibold text-xs text-gray-900 leading-snug line-clamp-2">
                        {event.title}
                      </p>

                      {/* Description */}
                      {event.description && (
                        <p className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                    </div>

                    {!hideEventActions && (
                      <div
                        className="shrink-0 pt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CalendarEventActions
                          event={event}
                          onEdit={onEditEvent}
                          onView={onViewEvent}
                          onDelete={onDeleteEvent}
                          refetch={refetch}
                          access={access}
                          size="sm"
                          variant="dropdown"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
